import React from 'react'
import { useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { IconChoices } from '../../common/Icon'
import { Button } from '../../common/Button'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { ChatHeaderState, chatHeaderStateAtom } from '../atom'
import { useHamburger } from '../../../hooks/useHamburgerContext'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { PublicGroupsModal } from '../PublicGroupsModal'
import { useModal } from '../../../hooks/useModal'
import { FindArchiveChatsModal } from '../FindArchivedChatsModal'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { useSelectedChatId } from '../../../state/context/SelectedChatIdProvider'
import { useSocietyLoader } from '../../../navigation/society/hooks/useSocietyLoader'
import { ActiveTabBar, getConversationType } from '../../../views/chat'
import { cn } from '../../../helpers/utils'
import { reverseUrl } from '../../../navigation/reverseUrl'

interface ChatHeaderButtonsProps {
  activeTabBar: ActiveTabBar
  activeTab: number
  className?: string
}

export const ChatHeaderButtons = ({
  activeTabBar,
  activeTab,
  className,
}: ChatHeaderButtonsProps): React.ReactNode => {
  useSocietyLoader(undefined)
  const { translate } = useAppTranslation()
  const [, setChatHeaderState] = useAtom(chatHeaderStateAtom)
  const { setDisplaySidebar } = useHamburger()
  const isMobile = useIsMobile()
  const {
    show: showPublicGroupsModal,
    open: openPublicGroupsModal,
    close: closePublicGroupsModal,
  } = useModal()
  const {
    show: showFindArchivedChatsModal,
    open: openFindArchivedChatsModal,
    close: closeFindArchivedChatsModal,
  } = useModal()

  const { society: selectedSociety } = useCurrentSociety()
  const { setSelectedChatRoomId } = useSelectedChatId()
  const conversationType = getConversationType(activeTabBar, activeTab)

  const startNewChatLabel = {
    oneonone: 'chatRoomsSidebar.startNewChat',
    interests: 'chatRoomsSidebar.startNewInterestsChat',
    board: 'chatRoomsSidebar.startNewBoardChat',
  }[conversationType] as
    | 'chatRoomsSidebar.startNewChat'
    | 'chatRoomsSidebar.startNewInterestsChat'
    | 'chatRoomsSidebar.startNewBoardChat'

  const hideSideBarOnMobile = (): void => {
    if (isMobile) {
      setDisplaySidebar(false)
    }
  }
  const navigate = useNavigate()

  const onStartNewChatClick = (): void => {
    setSelectedChatRoomId(undefined)
    setChatHeaderState(
      {
        board: ChatHeaderState.StartNewBoardChat,
        interests: ChatHeaderState.StartNewInterestsChat,
        oneonone: ChatHeaderState.StartNewChat,
      }[conversationType]
    )
    hideSideBarOnMobile()
    navigate(reverseUrl('chat'))
  }

  const onFindArchivedChatClick = (): void => {
    openFindArchivedChatsModal()
    setChatHeaderState(
      activeTabBar === ActiveTabBar.RESIDENT
        ? ChatHeaderState.FindArchivedChatsResident
        : ChatHeaderState.FindArchivedChats
    )
  }

  const onCloseFindArchivedChatsModal = (): void => {
    setChatHeaderState(ChatHeaderState.None)
    closeFindArchivedChatsModal()
  }

  return (
    <>
      <div className={cn(className, 'mx-6 flex flex-wrap gap-2')}>
        <Button
          disabled={!selectedSociety}
          label={translate(startNewChatLabel)}
          size="sm-wide"
          icon={IconChoices.PLUS_SIGN}
          onClick={onStartNewChatClick}
        />
        {conversationType === 'interests' && (
          <Button
            disabled={!selectedSociety}
            label={translate('chatRoomsSidebar.joinGroupChat')}
            size="sm-wide"
            icon={IconChoices.ARROW}
            onClick={openPublicGroupsModal}
          />
        )}
        <Button
          disabled={!selectedSociety}
          label={translate('chatRoomsSidebar.findQuestionsChat')}
          icon={IconChoices.ARROW}
          size="sm-wide"
          onClick={onFindArchivedChatClick}
        />
      </div>
      <PublicGroupsModal
        show={showPublicGroupsModal}
        close={closePublicGroupsModal}
        onItemSelect={closePublicGroupsModal}
      />
      <FindArchiveChatsModal
        show={showFindArchivedChatsModal}
        close={onCloseFindArchivedChatsModal}
        onItemSelect={onCloseFindArchivedChatsModal}
        type={conversationType}
      />
    </>
  )
}
